import { Banner, Button, ArrowRightIcon } from '@ag1/ui';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_PAGE_TYPE } from 'schema/subscription/pageType';
import { useLocale } from 'shared/hooks/useLocale';
import usePageId from 'shared/hooks/usePageId';

import { useQueryGetCommonContentData } from 'state/studio/commonContentData/getCommonContentData';
import styles from './BannerUpdatePaymentV2.module.scss';

const { SUBSCRIPTION_DETAILS } = SUBSCRIPTION_PAGE_TYPE;

interface BannerUpdatePaymentV2Props {
  page: SUBSCRIPTION_PAGE_TYPE.SUBSCRIPTION_DETAILS | SUBSCRIPTION_PAGE_TYPE.SUBSCRIPTION_LIST;
  hasExpiringPayment?: boolean;
  hasDeclinedPayment?: boolean;
}

export const BannerUpdatePaymentV2 = ({
  page,
  hasExpiringPayment,
  hasDeclinedPayment
}: BannerUpdatePaymentV2Props) => {
  const locale = useLocale();
  const contractId = usePageId();
  const router = useRouter();

  const { t } = useTranslation('banners');
  const { data: commonContentData } = useQueryGetCommonContentData({ locale });

  const content = commonContentData?.banners;

  const openModalLink = useMemo(
    () =>
      page === SUBSCRIPTION_DETAILS
        ? `/members/subscriptions/${contractId}?action=update-payment`
        : '/members/subscriptions?action=update-payment',
    [page, contractId]
  );

  const bannerLabel = useMemo(
    () => (hasExpiringPayment ? content?.expiredCard : content?.declinedCard || ''),
    [hasExpiringPayment, content]
  );

  const onClick = useCallback(() => {
    router.push(openModalLink);
  }, [router, openModalLink]);

  if ((!hasExpiringPayment && !hasDeclinedPayment) || !content) {
    return null;
  }

  return (
    <Banner
      className={styles.banner}
      variant="warning"
      label={bannerLabel}
      text={content?.updateMethod || ''}>
      <Button
        size="small"
        icon={<ArrowRightIcon />}
        iconPosition="right"
        theme="new"
        onClick={onClick}
        rounded>
        {t('paymentMethod.updateCtaBtnLabel')}
      </Button>
    </Banner>
  );
};
